import { toCamelCase, formToJSON } from 'web-utility';
import classNames from 'classnames';
import { PureComponent, SyntheticEvent } from 'react';
import { Table, Form, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faStar,
  faSortUp,
  faSortDown,
  faSort
} from '@fortawesome/free-solid-svg-icons';

import { IconButton } from '../../../component/IconButton';
import { IconInput } from '../../../component/IconInput';
import * as styles from './index.module.less';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import { Market, MarketSetting } from '../../../model/canister/Market';
import { KLine, TickSpaceIndex } from '../../../model/canister/KLine';

interface MarketItem {
  fav: boolean;
  top: boolean;
  token: string;
  lastPrice: number;
  change: number;
  setting?: MarketSetting;
}

const filterHead = ['Fav', 'Top', 'USDT', 'USDC', 'ICP'] as const,
  tableHead = ['Token', 'Last Price', 'Change'] as const;

type TableHeadType = typeof tableHead[number];
type FilterHeadType = typeof filterHead[number];

@observer
export class MainSearch extends PureComponent {
  @observable
  data: MarketItem[] = [];
  @observable
  ascend: boolean = true;
  @observable
  sortFiled: TableHeadType = 'Last Price';
  @observable
  filter?: FilterHeadType = undefined;

  marketService: Market = new Market();

  priceUpdateTimer?: NodeJS.Timer;

  @computed
  get dataWithFilter() {
    const lowerFilter = this.filter.toLowerCase();
    switch (this.filter) {
      case 'Fav':
      case 'Top':
        return this.data.filter(d => d[lowerFilter]);
      case 'ICP':
      case 'USDC':
      case 'USDT':
        return this.data.filter(d => d.token === this.filter);
      default:
        return this.data;
    }
  }

  async componentDidMount() {
    let DEFAULT_DATA: MarketItem[] = [
      {
        fav: true,
        top: true,
        token: 'DBTC',
        lastPrice: 0.0,
        change: 0.0
      },
      {
        fav: true,
        top: true,
        token: 'DETH',
        lastPrice: 0.0,
        change: 0.0
      }
    ];

    const marketList = await this.marketService.getMarketList();
    DEFAULT_DATA[0].setting = marketList[0];
    DEFAULT_DATA[1].setting = marketList[1];
    this.data = DEFAULT_DATA;

    this.priceUpdateTimer = setInterval(async () => {
      this.data.forEach(async d => {
        if (!d.setting?.klineCanisterId) return;
        const kline = new KLine(d.setting.klineCanisterId);
        const tick = await kline.fetchCurrentTick(TickSpaceIndex.OneDay);

        if (!tick) return;
        const priceChanged = tick.close.minus(tick.open).div(tick.open);
        d.lastPrice = tick.close
          .shiftedBy(-d.setting.priceDecimals)
          .dp(d.setting.orderPricePrecision)
          .toNumber();
        d.change = priceChanged.toNumber();
      });
    }, 10 * 1000);
  }

  componentWillUnmount() {
    if (this.priceUpdateTimer) clearInterval(this.priceUpdateTimer);
  }

  searchCoin = async (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  onFilterChanged = (filter: FilterHeadType) => {
    this.filter = filter;
  };

  onSortFiledChanged(sortFiled: TableHeadType) {
    this.sortFiled = sortFiled;

    const theFiled = toCamelCase(sortFiled);
    this.ascend = this.sortFiled === sortFiled ? !this.ascend : true;
    const diff = this.ascend ? -1 : 1;
    this.data = this.data.sort((a, b) =>
      a[theFiled] > b[theFiled] ? diff : -diff
    );
  }

  toggleFav(token: string) {
    this.data = this.data.map(d =>
      d.token === token ? { ...d, fav: !d['fav'] } : d
    );
  }

  redirectMarket(baseTokenId: string, quoteTokenId: string) {
    window.location.hash = `/market/${baseTokenId}/${quoteTokenId}`;
  }

  renderList = (
    { fav, token, lastPrice, change, setting }: MarketItem,
    index: number
  ) => (
    <tr
      key={index + lastPrice}
      className="text-secondary"
      onClick={() =>
        this.redirectMarket(setting?.baseTokenId, setting?.quoteTokenId)
      }
    >
      <td className="align-middle">
        <IconButton
          icon={faStar}
          color={fav ? 'light' : undefined}
          onClick={() => this.toggleFav(token)}
        />
        {token}
      </td>
      <td className="align-middle">{lastPrice}</td>
      <td className={`align-middle text-${change > 0 ? 'success' : 'danger'}`}>
        <FontAwesomeIcon
          icon={change > 0 ? faSortUp : faSortDown}
          className="pe-2"
        />
        {(change * 100).toFixed(2) + '%'}
      </td>
    </tr>
  );

  render() {
    const { data, sortFiled, filter, ascend } = this;

    return (
      <div style={{ width: '18.75rem' }} className={styles['main-search-wrap']}>
        <Form onSubmit={this.searchCoin}>
          <IconInput
            className=""
            icon={faSearch}
            name="searchCoin"
            type="search"
            aria-label="searchCoin"
            placeholder="Search"
            required
          />
        </Form>

        <ToggleButtonGroup
          className={styles['toggle-button-group']}
          type="radio"
          name="searchFilter"
          onChange={this.onFilterChanged}
        >
          {filterHead.map((head, index) => (
            <ToggleButton
              key={index + head}
              id={`radio${head}`}
              className={classNames(
                'border-0',
                head !== filter && 'text-secondary'
              )}
              variant="outline-secondary"
              size="sm"
              value={head}
              checked={filter === head}
            >
              {head}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Table borderless hover className={styles['table']}>
          <thead>
            <tr>
              {tableHead.map((header, index) => (
                <td
                  key={index + header}
                  className="text-secondary"
                  style={{ minWidth: '6rem' }}
                >
                  {header}
                  <IconButton
                    icon={
                      this.sortFiled === header
                        ? ascend
                          ? faSortUp
                          : faSortDown
                        : faSort
                    }
                    onClick={() => this.onSortFiledChanged(header)}
                  />
                </td>
              ))}
            </tr>
          </thead>
          <tbody>{data.map(this.renderList)}</tbody>
        </Table>
      </div>
    );
  }
}
